import { Button, Drawer, Loading } from "@components";
import ComboFilters from "pages/Reports/ComboFilters";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import { RFVProfilesDetailsChart } from "./RFVProfilesDetailsChart";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { ReportsAPI } from "providers/ReportsAPI";
import { Numbers } from "shared/utils/Numbers";
import { If } from "shared/components/control-statements";
import { Currency } from "shared/utils/Currency";
import classNames from "classnames/bind";
const css = classNames.bind(styles);

const Trend = ({ growth }) => {
  const isNegative = growth < 0;
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? "#e48186" : "#51D9A3";
  const sign = growth > 0 ? "+" : "";

  if (+growth === 0 || !growth) return null;

  return (
    <div className={css({ growth: true, decrease: isNegative })}>
      {sign}
      {Numbers.number(growth, 2)}%{" "}
      <i className={iconClass} style={{ color: iconColor }} />
    </div>
  );
};

const buildTable = (data) => {
  const statuses = {
    campeoes: `Campeões`,
    promissores: `Promissores`,
    requer_atencao: `Requer atenção`,
    em_risco: `Em risco`,
    desligando: `Desligando`,
    hibernando: `Hibernando`,
    abandonados: `Abandonados`,
  };

  const rows = [
    "campeoes",
    "promissores",
    "requer_atencao",
    "em_risco",
    "desligando",
    "hibernando",
    "abandonados",
  ];
  const columns = [
    "recency",
    "frequency",
    "average_ticket",
    "quantity",
    "total",
  ];

  const formatValue = (column, value) => {
    if (value === undefined) return "-";
    switch (column) {
      case "recency":
      case "frequency":
        return `${Math.round(Number(value))} dias`;
      case "average_ticket":
      case "total":
        return Currency.formatCurrency(value);
      case "quantity":
        return Number(value) % 1 === 0
          ? `${Number(value).toLocaleString("pt-BR")}`
          : Number(value).toFixed(1).toLocaleString("pt-BR");
      default:
        return value;
    }
  };

  const table = rows.map((row) => {
    return {
      alias: row,
      status: statuses[row],
      values: columns.map((column) => {
        const rawValue = data?.[row]?.[column]?.value ?? 0;
        const growth = data?.[row]?.[column]?.growth ?? 0;

        return {
          tuple: `${row}__${column}`,
          value: rawValue,
          formatted: formatValue(column, rawValue),
          growth,
        };
      }),
    };
  });

  return table;
};

export default function RFVProfilesDetails({ context }) {
  const months = getAllMonthsUntilNow(false);

  const [selectedData, setSelectedData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(months[0]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const { brand, unit } = context || {};

      setLoading(true);
      const provider = new ReportsAPI();
      const response = await provider.rfvDetails(
        selectedMonth.year,
        selectedMonth.month,
        brand?.id,
        unit?.id
      );
      setRawData(response);
      setLoading(false);
    };

    fetchData();
  }, [selectedMonth]);

  const { scrollRef, onMouseDown } = useDragScroll();

  return (
    <div className={styles.box2}>
      <div className="d-flex align-center justify-between px-15">
        <div className="d-flex align-center relative gap-5">
          <p className={styles.label}>Detalhamento de perfis RFV</p>
          <span
            className={styles.info}
            title="Compare recência, frequência, valor, quantidade de pedidos e
              faturamento de cada perfil RFV."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <div className="d-flex align-center">
          <ComboFilters
            options={months}
            onSelect={(option) => setSelectedMonth(option)}
            selectedOption={selectedMonth}
          />
          <div className={styles.divider} />
          <Button
            design="default-icon-mini-3"
            onClick={() =>
              setSelectedData({
                name: "Detalhamento de perfis RFV",
              })
            }
          >
            <i className="fas fa-chart-waterfall" />
          </Button>
        </div>
      </div>

      <div className={styles.hairline} />

      {loading ? (
        <div
          className="d-flex align-center justify-center"
          style={{ height: 338 }}
        >
          <Loading />
        </div>
      ) : (
        <>
          <div
            className={styles.table}
            onMouseDown={onMouseDown}
            ref={scrollRef}
          >
            <table className={styles.rfv}>
              <thead>
                <tr>
                  <th>PERFIL RFV</th>
                  <th>
                    <div className="d-flex align-center gap-5 relative">
                      Último pedido há{" "}
                      <span
                        className={styles.info}
                        title="Mediana de tempo sem pedir."
                      >
                        <i className="fal fa-info-circle" />
                      </span>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-center gap-5 relative">
                      INTERVALO ENTRE COMPRAS{" "}
                      <span
                        className={styles.info}
                        title="Média de intervalo entre compras."
                      >
                        <i className="fal fa-info-circle" />
                      </span>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-center gap-5 relative">
                      TICKET MÉDIO{" "}
                      <span className={styles.info} title="Mediana do ticket.">
                        <i className="fal fa-info-circle" />
                      </span>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-center gap-5 relative">
                      PEDIDOS P/ CLIENTE{" "}
                      <span
                        className={styles.info}
                        title="Média de pedidos por cliente."
                      >
                        <i className="fal fa-info-circle" />
                      </span>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-center gap-5 relative">
                      FATURAMENTO MÉDIO
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ height: 15 }} />
                {buildTable(rawData).map((row, idx) => (
                  <tr key={row.status}>
                    <td
                      className={styles.value}
                      style={{
                        textAlign: "left",
                        paddingLeft: 15,
                        minWidth: 230,
                      }}
                      key={idx}
                    >
                      {row.status}
                    </td>
                    {row.values.map(
                      ({ tuple, value, formatted, growth }, idx) => {
                        return (
                          <td
                            className={styles.value}
                            key={idx}
                            style={{ minWidth: 230 }}
                          >
                            <div className="d-flex items-center gap-5">
                              {formatted ?? "---"}
                              <If cond={growth}>
                                <Trend value={growth} />
                              </If>
                            </div>
                          </td>
                        );
                      }
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <RFVProfilesDetailsChart
          selectedData={selectedData}
          onClose={() => setSelectedData(false)}
          context={context}
          startOn2025
        />
      </Drawer>
    </div>
  );
}
